import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/SupabaseClient";
import { useAuth } from "../context/AuthContext";

// ✅ Pages
import CandidatesList from "./CandidatesList";
import CandidateInvite from "./CandidateInvite";
import ProctorsPage from "./ProctorsPage";
import TestsPage from "./TestsPage";
import QuestionsPage from "./QuestionsPage";
import BanksPage from "./BanksPage";
import AssignQuestionsPage from "./AssignQuestionsPage";
import ImportExamPage from "./ImportExamPage";
import ImportCandidatesPage from "./ImportCandidatesPage";
import AssignedCandidatesPage from "./AssignedCandidatesPage";

// ✅ Lucide Icons
import {
    LayoutDashboard,
    Users,
    FilePlus,
    ClipboardList,
    BookOpen,
    Banknote,
    ListChecks,
    Upload,
    UserCheck,
} from "lucide-react";

interface UserInfo {
    id: string;
    email: string;
    role: string;
    phone: string;
    organization_id?: number | null;
    organization?: { id: number; name: string } | null;
}

export default function Dashboard() {
    const { user, role, token, setUserAndRole, signOut } = useAuth();
    const nav = useNavigate();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState("overview");
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);

    useEffect(() => {
        if (role === "Candidate") {
            nav("/candidate/dashboard", { replace: true });
        }
    }, [role, nav]);

    useEffect(() => {
        async function loadProfile() {
            try {
                if (!token) {
                    nav("/login");
                    return;
                }

                // 🧩 Step 1: Load user record
                const { data: userRow, error: userError } = await supabase
                    .from("users")
                    .select("id, email, role, phone, organization_id")
                    .eq("id", user?.id)
                    .maybeSingle();

                if (userError) throw userError;

                // 🧩 Step 2: Load organization info
                let orgRow: { id: number; name: string } | null = null;
                if (userRow?.organization_id) {
                    const { data: orgData } = await supabase
                        .from("organizations")
                        .select("id, name")
                        .eq("id", userRow.organization_id)
                        .maybeSingle();
                    orgRow = orgData ?? null;
                }

                // 🧩 Step 3: Strongly typed user object
                const fullProfile: UserInfo = {
                    id: userRow?.id ?? "",
                    email: userRow?.email ?? "",
                    role: userRow?.role ?? "ExamAdmin",
                    phone: userRow?.phone ?? "",
                    organization_id: userRow?.organization_id ?? null,
                    organization: orgRow,
                };

                setUserInfo(fullProfile);
                setUserAndRole(
                    fullProfile,
                    fullProfile.role,
                    fullProfile.organization ?? null,
                    token
                );
            } catch (err: any) {
                setError(err.message);
                nav("/login");
            } finally {
                setLoading(false);
            }
        }

        if (role !== "Candidate") loadProfile();
    }, [token, role, nav, user, setUserAndRole]);

    async function handleLogout() {
        await signOut();
        nav("/login", { replace: true });
    }

    if (loading) return <p className="p-6 text-gray-600">Loading dashboard...</p>;
    if (error) return <p className="p-6 text-red-600">{error}</p>;

    // ✅ Define dashboard tabs with icons
    const tabs =
        role === "SuperAdmin" || role === "ExamAdmin"
            ? [
                { id: "overview", label: "Overview", icon: <LayoutDashboard size={16} /> },
                { id: "candidates", label: "Candidates", icon: <Users size={16} /> },
                { id: "invite", label: "Invite", icon: <FilePlus size={16} /> },
                { id: "proctors", label: "Proctors", icon: <ClipboardList size={16} /> },
                { id: "tests", label: "Tests", icon: <BookOpen size={16} /> },
                { id: "questions", label: "Questions", icon: <ClipboardList size={16} /> },
                { id: "banks", label: "Banks", icon: <Banknote size={16} /> },
                { id: "assign", label: "Assign", icon: <ListChecks size={16} /> },
                { id: "import", label: "Import", icon: <Upload size={16} /> },
                {
                    id: "import-candidates",
                    label: "Import Candidates",
                    icon: <UserCheck size={16} />,
                },
                { id: "assigned", label: "Assigned", icon: <ClipboardList size={16} /> },
            ]
            : [{ id: "overview", label: "Overview", icon: <LayoutDashboard size={16} /> }];

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900">
            {/* 🔹 Header Bar */}
            <div className="flex justify-between items-center bg-gradient-to-r from-indigo-700 via-indigo-600 to-violet-600 text-white px-6 py-3 shadow-sm">
                <div className="flex items-center gap-3">
                    <img
                        src="/Images/ExamsPro.png"
                        alt="ExamsPro Logo"
                        className="w-9 h-9 rounded-md bg-white p-1 shadow-sm"
                    />
                    <div className="flex flex-col leading-tight">
                        <span className="text-lg sm:text-xl font-semibold tracking-tight">
                            ExamsPro Dashboard
                        </span>
                        <span className="text-xs text-indigo-100">
                            by SecureID Technologies
                        </span>
                    </div>
                </div>

                <button
                    onClick={handleLogout}
                    className="bg-white/10 hover:bg-white/20 px-4 py-1.5 rounded text-sm font-medium transition"
                >
                    Logout
                </button>
            </div>

            {/* 🔹 User Info */}
            {userInfo && (
                <section className="bg-indigo-50 border-l-4 border-indigo-700 rounded-md mx-6 my-6 p-6 shadow-sm">
                    <h2 className="text-lg font-semibold text-indigo-700 mb-3">
                        User Information
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm sm:text-base">
                        <p className="text-gray-800">
                            <strong className="text-indigo-700">Email:</strong> {userInfo.email}
                        </p>
                        <p className="text-gray-800">
                            <strong className="text-indigo-700">Role:</strong> {userInfo.role}
                        </p>
                        <p className="text-gray-800">
                            <strong className="text-indigo-700">Organization ID:</strong>{" "}
                            {userInfo.organization?.id ?? "None"}
                        </p>
                        <p className="text-gray-800">
                            <strong className="text-indigo-700">Organization:</strong>{" "}
                            {userInfo.organization?.name ?? "None"}
                        </p>
                        <p className="text-gray-800">
                            <strong className="text-indigo-700">Phone:</strong>{" "}
                            {userInfo.phone ?? "N/A"}
                        </p>
                    </div>
                </section>
            )}

            {/* 🔹 Tabs with Icons */}
            <div className="flex overflow-x-auto gap-4 border-b border-indigo-200 mx-6 mb-6 pb-1 scrollbar-thin">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-3 pb-2 text-sm sm:text-base capitalize whitespace-nowrap transition-colors ${activeTab === tab.id
                                ? "border-b-2 border-indigo-600 text-indigo-700 font-semibold"
                                : "text-gray-600 hover:text-indigo-600"
                            }`}
                    >
                        {tab.icon}
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* 🔹 Tab Content */}
            <div className="mx-6 text-sm sm:text-base mb-10">
                {activeTab === "overview" && (
                    <div className="bg-white rounded-md p-6 shadow-sm border border-gray-100">
                        <h2 className="text-lg font-semibold text-indigo-700 mb-2">
                            Overview
                        </h2>
                        <p className="text-gray-700">
                            Welcome to your ExamAdmin dashboard. Use the tabs above to manage
                            candidates, proctors, and tests.
                        </p>
                    </div>
                )}

                {activeTab === "candidates" && <CandidatesList />}
                {activeTab === "invite" && <CandidateInvite />}
                {activeTab === "proctors" && <ProctorsPage />}
                {activeTab === "tests" && <TestsPage />}
                {activeTab === "banks" && <BanksPage />}
                {activeTab === "questions" && <QuestionsPage />}
                {activeTab === "assign" && <AssignQuestionsPage />}
                {activeTab === "import" && (
                    <ImportExamPage
                        organizationId={userInfo?.organization?.id || 0}
                        createdBy={userInfo?.id || ""}
                    />
                )}
                {activeTab === "import-candidates" && <ImportCandidatesPage />}
                {activeTab === "assigned" && <AssignedCandidatesPage />}
            </div>
        </div>
    );
}
